import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TopLevelRoutes, UsersIamDivisionChoice } from '@r1-compass-library/models';
import { LocalStorageService } from '@r1-compass-library/services/local-storage.service';

export type Page = {
    name: string;
    url: string;
    rootFolderId?: string;
};

@Component({
    selector: 'r1-compass-lib-header',
    imports: [
        MatToolbarModule,
        RouterModule,
        MatButtonModule,
        MatTooltipModule,
        MatMenuModule,
        MatIconModule,
        NgOptimizedImage,
    ],
    templateUrl: './header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        .spacer {
            flex: 1 1 auto;
        }

        .header {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            background-color: var(--r1-black);
            color: var(--r1-white);
            display: flex;
        }

        #division-picker {
            button {
                color: var(--r1-white);
            }
        }
    `,
})
export class HeaderComponent {
    readonly #localStorageService = inject(LocalStorageService);

    pages = input.required<Page[]>();
    selectedDivisionCode = input.required<string>();
    divisionChoices = input.required<UsersIamDivisionChoice[]>();
    currentEntityHomepage = input.required<Page>();

    switchDivision = output<void>();
    logOut = output<void>();

    private pagesAllowedByJWT = computed(() => {
        const jwt = this.#localStorageService.jwtClaims();
        const pages = this.pages();

        const jwtPaths = jwt?.recs.map(rec => rec.path) ?? [];
        const allowedPages = pages.filter(page => {
            const matchingTopLevelClaim =
                TopLevelRoutes.find(topRoute => page.url.replace('/', '').includes(topRoute.path))?.claim ?? '';
            return jwtPaths.includes(matchingTopLevelClaim);
        });

        return allowedPages;
    });

    protected pagesWithPermissions = computed(() =>
        this.pages().map(page =>
            this.pagesAllowedByJWT().includes(page) ? { ...page, allowed: true } : { ...page, allowed: false }
        )
    );
}
